// Share-token codec — the single source of truth for the reform-sharing
// URL format (#244). Both encode (Copy share link) and decode (restore from
// a shared URL) live here, in one language, so there is no cross-process
// format contract to drift. R is not involved in the token.
//
// Format:  s = base64url(JSON.stringify({ v, app, eng, c }))
//   v   — token schema version (bump on a breaking format change)
//   app — app version that produced the token   (reproducibility fingerprint)
//   eng — engine version that produced the token (        "              )
//   c   — { [controlKey]: controlValue } for *modified* controls only
//
// controlKey is each island's Shiny inputId (already globally unique and
// derived from stable spec ids); controlValue is the raw value the user set
// (a delta %, an absolute number, or { boundaries, rates }) so a restore puts
// the control back to exactly what was shared.
//
// SECURITY: decodeShareToken is the trust boundary for an attacker-craftable
// URL. It never throws, caps input size before and after decode, and skips
// implausibly-shaped values rather than failing the whole token. Per-island
// validation (range clamping) and a server-side clamp before the engine are
// the second and third layers — see share_restore.ts and transforms.R.

export const SHARE_TOKEN_VERSION = 1;

// Caps sized well above any real reform (a couple dozen instruments, each a
// handful of brackets) but far below anything that could exhaust memory when
// an island expands the decoded value.
const MAX_TOKEN_LENGTH = 8192;
const MAX_CONTROLS = 128;
const MAX_ARRAY_LENGTH = 64;

export interface ShareFingerprint {
  app: string;
  eng: string;
}

/** Modified-control map, ready to encode. Values are island-shaped. */
export type ShareControls = Record<string, unknown>;

export interface DecodedToken {
  app: string;
  eng: string;
  /** Only structurally-plausible control values survive decode. */
  controls: Record<string, unknown>;
}

function base64UrlEncode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(input: string): string {
  const padded = input
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(input.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function isBoundedNumberArray(v: unknown): v is number[] {
  return (
    Array.isArray(v) &&
    v.length <= MAX_ARRAY_LENGTH &&
    v.every(isFiniteNumber)
  );
}

// A decoded control value is "plausible" if it matches one of the shapes an
// island can consume: a finite number (delta / absolute), a bracket commit
// { boundaries, rates }, or a flat string→string map (categorical assignment).
// Anything else (nested objects, oversized arrays, non-finite numbers) is
// dropped so it never reaches an island's state or setInputValue.
function isPlausibleControlValue(v: unknown): boolean {
  if (isFiniteNumber(v)) return true;
  if (isRecord(v)) {
    const keys = Object.keys(v);
    if (keys.length > MAX_CONTROLS) return false;
    if ("boundaries" in v || "rates" in v) {
      return isBoundedNumberArray(v.boundaries) && isBoundedNumberArray(v.rates);
    }
    return keys.every((k) => typeof v[k] === "string");
  }
  return false;
}

export function encodeShareToken(
  controls: ShareControls,
  fingerprint: ShareFingerprint,
): string {
  return base64UrlEncode(
    JSON.stringify({
      v: SHARE_TOKEN_VERSION,
      app: fingerprint.app,
      eng: fingerprint.eng,
      c: controls,
    }),
  );
}

/**
 * Decode a `s=` token. Returns null for anything we can't trust:
 * absent, oversized, malformed base64/JSON, wrong version, or wrong shape.
 * Individual implausible control values are skipped, not fatal.
 */
export function decodeShareToken(raw: string | null | undefined): DecodedToken | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_TOKEN_LENGTH) {
    return null;
  }
  let json: string;
  try {
    json = base64UrlDecode(raw);
  } catch {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.v !== SHARE_TOKEN_VERSION) return null;
  if (!isRecord(parsed.c)) return null;

  const entries = Object.entries(parsed.c);
  if (entries.length > MAX_CONTROLS) return null;

  const controls: Record<string, unknown> = {};
  for (const [key, value] of entries) {
    if (isPlausibleControlValue(value)) controls[key] = value;
  }
  return {
    app: typeof parsed.app === "string" ? parsed.app : "",
    eng: typeof parsed.eng === "string" ? parsed.eng : "",
    controls,
  };
}
